/**
 * Home: the launcher tab (path `/`, empty query; D7).
 * Header + four method cards + Recent searches / Starred lists panels.
 * Inside a tab a link replaces Home in place and keeps the tab id (D5).
 * With zero tabs there is no tab router, so a link opens a tab instead
 * (and no saved-list hand-off runs: the new tab's id is unknown).
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useDbUser } from '../hooks/useDbUser';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { useSavedLists } from '../hooks/useSavedLists';
import { handOffSavedList, replayPath, savedListPath } from '../utils/searchNav';
import { useTab } from './TabContext';
import { ShellNavLink } from './home/ShellNavLink';
import { SearchHistoryRow } from './home/SearchHistoryRow';
import { SavedListRow } from './home/SavedListRow';
import { FromPrecepi } from './home/FromPrecepi';
import { FROM_PRECEPI_ITEMS, FROM_PRECEPI_ALL_UPDATES_URL } from './home/fromPrecepiItems';

const LAUNCHERS: {
  id: string; label: string; to: string; desc: string; example: string; beta?: boolean; adminOnly?: boolean;
}[] = [
  {
    id: 'keyword', label: 'Keyword', to: '/?mode=keyword',
    desc: 'Find exact wording in label text, with Boolean, proximity and synonym options.',
    example: '"ocular toxicity" NOT keratitis',
  },
  {
    id: 'cogni', label: 'Cogni', to: '/?mode=ai',
    desc: 'Ask a question in plain language and get matching labels with the passage that answers it.',
    example: 'Find examples where patient-reported outcomes are described in clinical studies in the USPI',
  },
  {
    id: 'investigator', label: 'Investigator', to: '/?mode=researcher',
    desc: 'Research a topic in depth: multi-step search across labels, letters and reviews.',
    example: 'Examples where an indication expanded to include populations not represented, or not fully represented, in the original clinical studies described in the label',
  },
  {
    id: 'agents', label: 'Agentic reporting', to: '/?mode=workstream',
    desc: 'Turn a question into a cited report you can edit, export and share.',
    example: "Across available USPI labels and their reviews, analyze the relationship between indication language and the clinical populations and endpoints described in the label's Clinical Studies section, with particular emphasis on products whose indication wording covers a broader population than the pivotal studies explicitly described",
    beta: true, adminOnly: true,
  },
];

const ALL_LINK = 'text-[13px] text-accent no-underline hover:underline';
const PANEL_HEAD = 'flex items-baseline justify-between border-b border-line pb-2';

export const Home: React.FC = () => {
  const { storageKey } = useTab();
  // Same gate as SearchPage's surface bar: shown while loading, hidden once a non-admin is confirmed.
  const { isAdmin, loading } = useDbUser();
  const { history: entries, isLoading: historyLoading } = useSearchHistory();
  const { lists, isLoading: listsLoading } = useSavedLists();
  return (
    <div data-testid="shell-home" className="mx-auto flex w-full max-w-[980px] flex-col gap-10 px-7 pb-16 pt-14">
      <header className="flex flex-col gap-2.5">
        <span className="font-mono text-[12px] uppercase tracking-[0.14em] text-accent">Labels · Letters · HA reviews</span>
        <h1 className="font-display text-[36px] font-medium leading-[1.15] tracking-[-0.02em] text-ink">How do you want to search?</h1>
      </header>
      <nav className="grid gap-[14px] [grid-template-columns:repeat(auto-fit,minmax(min(400px,100%),1fr))]">
        {LAUNCHERS.filter((l) => !l.adminOnly || isAdmin || loading).map((l) => (
          <ShellNavLink
            key={l.id}
            to={l.to}
            replace
            data-testid={`shell-launcher-${l.id}`}
            className="flex min-h-[148px] flex-col gap-2.5 rounded border border-line bg-white px-[22px] py-5 text-ink no-underline hover:border-accent-ring"
          >
            <span className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2">
                <span className="font-display text-[22px] font-medium tracking-[-0.01em]">{l.label}</span>
                {l.beta && (
                  <span className="rounded-ctl border border-signal/30 px-1.5 font-mono text-[10px] tracking-[0.08em] text-signal">BETA</span>
                )}
              </span>
              <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-ctl bg-ink text-ground">
                <ArrowRight size={16} aria-hidden="true" />
              </span>
            </span>
            <span className="text-[14px] leading-[1.5] text-ink-2">{l.desc}</span>
            <span title={l.example} className="mt-auto line-clamp-2 font-mono text-[12px] text-ink-2">{l.example}</span>
          </ShellNavLink>
        ))}
      </nav>
      <FromPrecepi items={FROM_PRECEPI_ITEMS} allUpdatesUrl={FROM_PRECEPI_ALL_UPDATES_URL} isAdmin={isAdmin} />
      <div className="grid gap-9 [grid-template-columns:repeat(auto-fit,minmax(min(380px,100%),1fr))]">
        <section data-testid="home-recent-searches" className="flex flex-col gap-2.5">
          <div className={PANEL_HEAD}>
            <h2 className="text-[13px] font-semibold">Recent searches</h2>
            {!historyLoading && entries.length > 0 && (
              <ShellNavLink to="/recent-searches" replace data-testid="home-recent-all" className={ALL_LINK}>
                All {entries.length}
              </ShellNavLink>
            )}
          </div>
          {!historyLoading && entries.length === 0 && <p className="text-[13px] text-ink-3">No searches yet.</p>}
          {!historyLoading && entries.slice(0, 3).map((e) => (
            <SearchHistoryRow key={e.id} entry={e} to={replayPath(e)} replace />
          ))}
        </section>
        <section data-testid="home-starred-lists" className="flex flex-col gap-2.5">
          <div className={PANEL_HEAD}>
            <h2 className="flex items-center gap-1.5 text-[13px] font-semibold">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#C98A1B" aria-hidden="true">
                <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
              </svg>
              Starred lists
            </h2>
            {!listsLoading && lists.length > 0 && (
              <ShellNavLink to="/starred-lists" replace data-testid="home-starred-all" className={ALL_LINK}>
                All {lists.length}
              </ShellNavLink>
            )}
          </div>
          {!listsLoading && lists.length === 0 && <p className="text-[13px] text-ink-3">No starred lists yet.</p>}
          {!listsLoading && lists.slice(0, 3).map((l) => (
            <SavedListRow
              key={l.id}
              list={l}
              to={savedListPath(l)}
              replace
              onBeforeNavigate={() => handOffSavedList(l, storageKey)}
            />
          ))}
        </section>
      </div>
    </div>
  );
};

export default Home;
